<template>
  <v-container fluid class="page">
    <div class="page-hero">
      <div>
        <h2 class="d-flex align-center">Listas de precios <HelpHint text="Administra listas base/virtuales, ítems, actualizaciones e historial de cambios de precio." /></h2>
        <p>Administra listas y precios por producto.</p>
      </div>
      <v-btn color="primary" class="hero-btn" @click="openCreate">Nueva lista</v-btn>
    </div>

    <v-card outlined class="page-card">
      <div class="table-header">
        <div>
          <h3>Listado</h3>
          <p>{{ lists.length }} listas</p>
        </div>
        <v-btn text color="primary" :loading="loading" @click="fetchLists">Recargar</v-btn>
      </div>

      <v-data-table
        :headers="headers"
        :items="lists"
        :loading="loading"
        class="elevation-0"
        item-key="id"
      >
        <template v-slot:item.isActive="{ item }">
          <span :class="['pill', item.isActive ? 'pill--success' : 'pill--warn']">
            {{ item.isActive ? 'Activa' : 'Inactiva' }}
          </span>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn text small color="primary" @click="openEdit(item)">Editar</v-btn>
          <v-btn text small color="primary" @click="openItems(item)">Items</v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="dialog" max-width="560">
      <v-card>
        <v-card-title>{{ isEditing ? 'Editar lista' : 'Crear lista' }}</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid" lazy-validation>
            <v-text-field v-model="form.code" label="Codigo" outlined dense :rules="requiredRules" />
            <v-text-field v-model="form.name" label="Nombre" outlined dense :rules="requiredRules" />
            <v-text-field v-model="form.shortDescription" label="Descripcion corta" outlined dense />
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field v-model="form.currencyCode" label="Moneda" outlined dense />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model.number="form.decimals" label="Decimales" outlined dense />
              </v-col>
            </v-row>
            <v-switch v-model="form.isVirtual" label="Es lista derivada (con fórmula/descuento)" inset />
            <v-row dense v-if="form.isVirtual">
              <v-col cols="12" md="6">
                <v-select
                  v-model="form.dependsOnListId"
                  :items="baseListsOptions"
                  item-text="label"
                  item-value="id"
                  label="Lista base"
                  outlined
                  dense
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model.number="form.percentageAdjustment"
                  type="number"
                  label="Ajuste % (Ej: +45 o -30)"
                  outlined
                  dense
                />
              </v-col>
            </v-row>
            <v-text-field 
              v-model="form.attributes" 
              label="Descuentos Matriz Mostrador (Separados por coma)" 
              placeholder="Ej: 46, 48, 51"
              hint="Define qué columnas de descuento ver en el mostrador. Déjalo vacío para no mostrar descuentos adicionales."
              persistent-hint
              outlined 
              dense
              class="mb-3"
            />
            <v-switch v-model="form.includesTax" label="Incluye impuestos" inset />
            <v-switch v-model="form.isActive" label="Lista activa" inset />
          </v-form>
          <div v-if="error" class="error-text">{{ error }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeDialog">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveList">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="itemsDialog" max-width="980">
      <v-card>
        <v-card-title class="items-header">
          Lista: {{ selectedList ? selectedList.name : '' }}
          <v-spacer />
          <v-btn text @click="itemsDialog = false">Cerrar</v-btn>
        </v-card-title>
        <v-card-text>
          <v-tabs v-model="itemsTab" background-color="transparent" class="mb-4">
            <v-tab>
              Elementos
              <v-tooltip bottom max-width="260">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="grey" class="ml-1" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                </template>
                <span>Gestión directa de precios de artículos.</span>
              </v-tooltip>
            </v-tab>
            <v-tab>
              Extendida
              <v-tooltip bottom max-width="260">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="grey" class="ml-1" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                </template>
                <span>Cruza precios con stock físico y depósitos.</span>
              </v-tooltip>
            </v-tab>
            <v-tab>
              Actualizaciones
              <v-tooltip bottom max-width="260">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="grey" class="ml-1" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                </template>
                <span>Registro de aumentos masivos aplicados.</span>
              </v-tooltip>
            </v-tab>
            <v-tab>
              Historial
              <v-tooltip bottom max-width="260">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="grey" class="ml-1" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                </template>
                <span>Bitácora de auditoría de cambios de precios.</span>
              </v-tooltip>
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="itemsTab">
            <v-tab-item>
              <div class="table-header">
                <div>
                  <h3>
                    Elementos
                    <v-tooltip bottom max-width="300">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small color="grey" class="ml-1" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                      </template>
                      <span>Aquí gestionas los precios de tus productos de forma directa. Puedes crear, editar, eliminar o usar el Asistente de Carga.</span>
                    </v-tooltip>
                  </h3>
                  <p>{{ items.length }} registrados</p>
                </div>
                <div class="d-flex align-center">
                  <v-btn color="primary" @click="openItemCreate">
                    <v-icon left>mdi-plus</v-icon>
                    Nuevo item
                  </v-btn>
                  <v-btn color="success" outlined @click="openWizard" class="ml-2">
                    <v-icon left>mdi-auto-fix</v-icon>
                    Asistente de Carga
                  </v-btn>
                  <v-btn color="primary" outlined class="ml-2" @click="openImport">
                    <v-icon left>mdi-file-upload-outline</v-icon>
                    Importar archivo
                  </v-btn>
                  <v-btn text color="primary" class="ml-2" :loading="exportLoading" @click="downloadImportTemplate">
                    <v-icon left>mdi-download-outline</v-icon>
                    Descargar plantilla
                  </v-btn>
                  <v-tooltip bottom max-width="300">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon small color="grey" class="ml-2" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                    </template>
                    <span>Inicializa masivamente artículos por Proveedor o Rubro heredando sus costos base de forma automática.</span>
                  </v-tooltip>
                </div>
              </div>

              <v-data-table
                :headers="itemHeaders"
                :items="items"
                :loading="itemsLoading"
                class="elevation-0"
                item-key="id"
              >
                <template v-slot:item.isActive="{ item }">
                  <span :class="['pill', item.isActive ? 'pill--success' : 'pill--warn']">
                    {{ item.isActive ? 'Activo' : 'Inactivo' }}
                  </span>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn text small color="primary" @click="openItemEdit(item)">Editar</v-btn>
                  <v-btn text small color="error" @click="deleteItem(item)">Eliminar</v-btn>
                </template>
              </v-data-table>
            </v-tab-item>

            <v-tab-item>
              <div class="table-header">
                <div>
                  <h3>
                    Extendida
                    <v-tooltip bottom max-width="300">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small color="grey" class="ml-1" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                      </template>
                      <span>Planilla cruzada que vincula los precios de esta lista con el stock físico disponible en cada uno de tus depósitos.</span>
                    </v-tooltip>
                  </h3>
                  <p>Filtra por stock y deposito.</p>
                </div>
                <div class="d-flex align-center" style="gap: 10px;">
                  <v-select
                    v-model="exportFormat"
                    :items="exportFormatOptions"
                    dense
                    outlined
                    hide-details
                    label="Formato"
                    style="max-width: 140px;"
                  />
                  <v-btn color="primary" :loading="exportLoading" @click="exportExtended">Descargar</v-btn>
                </div>
              </div>
              <div class="filters">
                <v-select
                  v-model="extendedFilters.hasStock"
                  :items="hasStockOptions"
                  dense
                  outlined
                  hide-details
                  label="Stock"
                />
                <v-select
                  v-model="extendedFilters.warehouseId"
                  :items="warehouseOptions"
                  item-text="label"
                  item-value="id"
                  dense
                  outlined
                  hide-details
                  clearable
                  label="Depósito"
                />
                <v-text-field
                  v-model="extendedFilters.q"
                  dense
                  outlined
                  hide-details
                  label="Busqueda"
                />
                <v-btn color="primary" :loading="extendedLoading" @click="fetchExtended">
                  Buscar
                </v-btn>
              </div>
              <v-data-table
                :headers="extendedHeaders"
                :items="extendedItems"
                :loading="extendedLoading"
                class="elevation-0"
                item-key="itemId"
              />
            </v-tab-item>

            <v-tab-item>
              <div class="table-header">
                <div>
                  <h3>
                    Actualizaciones
                    <v-tooltip bottom max-width="300">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small color="grey" class="ml-1" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                      </template>
                      <span>Registro histórico y confirmación de actualizaciones masivas de precios aplicadas a esta lista (ej. incrementos por proveedor o rubro).</span>
                    </v-tooltip>
                  </h3>
                  <p>{{ updates.length }} registros</p>
                </div>
                <v-btn text color="primary" :loading="updatesLoading" @click="fetchUpdates">Recargar</v-btn>
              </div>
              <v-alert v-if="updatesError" type="error" dense outlined class="mb-3">{{ updatesError }}</v-alert>
              <v-data-table
                :headers="updatesHeaders"
                :items="updates"
                :loading="updatesLoading"
                class="elevation-0"
                item-key="id"
              >
                <template v-slot:item.createdAt="{ item }">
                  {{ formatDateTime(item.createdAt) }}
                </template>
                <template v-slot:item.confirmed="{ item }">
                  <span :class="['pill', item.confirmed ? 'pill--success' : 'pill--warn']">
                    {{ item.confirmed ? 'Confirmada' : 'Pendiente' }}
                  </span>
                </template>
                <template v-slot:item.multiplier="{ item }">
                  {{ item.multiplier != null ? formatNumber(item.multiplier) : '—' }}
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn
                    v-if="!item.confirmed"
                    small
                    color="primary"
                    :loading="confirmingUpdateId === item.id"
                    @click="confirmUpdate(item)"
                  >
                    Confirmar
                  </v-btn>
                </template>
              </v-data-table>
            </v-tab-item>

            <v-tab-item>
              <div class="table-header">
                <div>
                  <h3>
                    Historial de cambios
                    <v-tooltip bottom max-width="300">
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small color="grey" class="ml-1" v-bind="attrs" v-on="on">mdi-help-circle-outline</v-icon>
                      </template>
                      <span>Bitácora detallada de auditoría. Registra la fecha exacta de cada modificación de precio, el usuario responsable y el cambio del valor anterior al nuevo.</span>
                    </v-tooltip>
                  </h3>
                  <p>{{ historyEntries.length }} registros</p>
                </div>
              </div>
              <v-row dense class="mb-3">
                <v-col cols="12" md="4">
                  <v-autocomplete
                    v-model="historyFilters.productId"
                    :items="productOptions"
                    item-text="label"
                    item-value="id"
                    clearable
                    label="Producto"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model.number="historyFilters.limit"
                    type="number"
                    label="Límite"
                    outlined
                    dense
                    hide-details
                    min="1"
                  />
                </v-col>
                <v-col cols="12" md="3" class="d-flex align-end">
                  <v-btn color="primary" :loading="historyLoading" @click="fetchHistory">Buscar</v-btn>
                </v-col>
              </v-row>
              <v-alert v-if="historyError" type="error" dense outlined class="mb-3">{{ historyError }}</v-alert>
              <v-data-table
                :headers="historyHeaders"
                :items="historyEntries"
                :loading="historyLoading"
                class="elevation-0"
                item-key="id"
              >
                <template v-slot:item.changedAt="{ item }">
                  {{ formatDateTime(item.changedAt) }}
                </template>
                <template v-slot:item.productLabel="{ item }">
                  {{ item.productLabel }}
                </template>
                <template v-slot:item.previousPrice="{ item }">
                  {{ formatNumber(item.previousPrice) }}
                </template>
                <template v-slot:item.newPrice="{ item }">
                  {{ formatNumber(item.newPrice) }}
                </template>
              </v-data-table>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="itemDialog" max-width="520">
      <v-card>
        <v-card-title>{{ itemEditing ? 'Editar item' : 'Crear item' }}</v-card-title>
        <v-card-text>
          <v-form ref="itemForm" v-model="itemValid" lazy-validation>
            <v-autocomplete
              v-model="itemFormState.productId"
              :items="productOptions"
              item-text="label"
              item-value="id"
              label="Producto"
              clearable
              outlined
              dense
              :rules="requiredRules"
            />
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field v-model.number="itemFormState.price" label="Precio" outlined dense />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="itemFormState.currencyCode" label="Moneda" outlined dense />
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12" md="6">
                <v-text-field v-model="itemFormState.validFrom" label="Valido desde" outlined dense />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="itemFormState.validTo" label="Valido hasta" outlined dense />
              </v-col>
            </v-row>
            <v-switch v-model="itemFormState.isActive" label="Item activo" inset />
          </v-form>
          <div v-if="itemError" class="error-text">{{ itemError }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="itemDialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="itemSaving" @click="saveItem">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialogo de Asistente de Carga Dinámico y Predictivo -->
    <v-dialog v-model="wizardDialog" max-width="580" overlay-color="rgba(15, 23, 42, 0.4)" overlay-opacity="1">
      <v-card class="rounded-xl px-4 py-3 shadow-2xl">
        <v-card-title class="headline d-flex align-center pb-2">
          <v-icon color="primary" class="mr-2">mdi-auto-fix</v-icon>
          <span class="font-weight-bold">Asistente de Carga Masiva</span>
        </v-card-title>
        
        <v-card-text>
          <p class="text-subtitle-2 text-secondary mb-4">
            Pobla la lista de precios <strong>{{ selectedList ? selectedList.name : '' }}</strong> asociando artículos masivamente de acuerdo a tu criterio. El sistema les asignará su costo base.
          </p>

          <v-form ref="wizardForm" class="mt-2">
            <v-subheader class="pl-0 font-weight-bold grey--text text--darken-2">¿Qué artículos deseas cargar?</v-subheader>
            <v-radio-group v-model="wizardForm.scopeType" row mandatory class="mt-0 mb-4">
              <v-radio label="Todos los artículos" value="all" color="primary"></v-radio>
              <v-radio label="Por Proveedor" value="provider" color="primary"></v-radio>
              <v-radio label="Por Rubro / Categoría" value="rubro" color="primary"></v-radio>
            </v-radio-group>

            <!-- Sección Proveedor (Predictivo) -->
            <v-expand-transition>
              <div v-if="wizardForm.scopeType === 'provider'" class="mb-4">
                <v-subheader class="pl-0 font-weight-bold grey--text text--darken-2">Selecciona el Proveedor</v-subheader>
                <v-autocomplete
                  v-model="wizardForm.providerId"
                  :items="providerOptions"
                  item-text="label"
                  item-value="id"
                  placeholder="Escribe el nombre o código del proveedor..."
                  outlined
                  dense
                  clearable
                  hide-details
                  class="rounded-lg"
                  no-data-text="No se encontraron proveedores que coincidan"
                />
              </div>
            </v-expand-transition>

            <!-- Sección Rubro / Categoría (Predictivo) -->
            <v-expand-transition>
              <div v-if="wizardForm.scopeType === 'rubro'" class="mb-4">
                <v-subheader class="pl-0 font-weight-bold grey--text text--darken-2">Selecciona el Rubro</v-subheader>
                <v-autocomplete
                  v-model="wizardForm.rubro"
                  :items="rubroOptions"
                  item-text="label"
                  item-value="value"
                  placeholder="Escribe el nombre del rubro..."
                  outlined
                  dense
                  clearable
                  hide-details
                  class="rounded-lg"
                  no-data-text="No se encontraron rubros que coincidan"
                />
              </div>
            </v-expand-transition>
          </v-form>
        </v-card-text>

        <v-card-actions class="pt-0">
          <v-spacer />
          <v-btn text class="rounded-lg font-weight-bold px-4" @click="wizardDialog = false" :disabled="wizardSaving">
            Cancelar
          </v-btn>
          <v-btn color="primary" class="rounded-lg font-weight-bold px-6 shadow" :loading="wizardSaving" @click="applyWizardInitialize">
            <v-icon left>mdi-check</v-icon>
            Iniciar Carga
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="importDialog" max-width="1050" persistent>
      <v-card>
        <v-card-title>Importar precios y catálogo</v-card-title>
        <v-card-text>
          <p class="mb-3">Acepta la plantilla ERP y exportaciones WooCommerce. El artículo se identifica únicamente por Código/SKU.</p>
          <v-file-input v-model="importFile" accept=".csv,.xlsx,.xls" label="Archivo CSV o Excel" outlined dense show-size />
          <v-checkbox v-model="importProductFields" label="Actualizar también nombre, descripción e imagen principal cuando el archivo los incluya" />
          <v-alert v-if="importResult" type="info" dense outlined>
            Formato detectado: <strong>{{ importResult.sourceFormat }}</strong>. {{ importResult.totalRows }} filas validadas. Los códigos duplicados, inexistentes o precios inválidos bloquean la carga completa.
          </v-alert>
          <v-data-table v-if="importResult" :headers="importHeaders" :items="importResult.previewRows || []" dense :items-per-page="10">
            <template v-slot:item.status="{ item }"><span :class="item.status === 'matched' ? 'pill pill--success' : 'pill pill--warn'">{{ item.status === 'matched' ? 'Coincide' : item.status === 'duplicate_omitted' ? 'Se omitirá duplicado' : 'No encontrado' }}</span></template>
            <template v-slot:item.currentPrice="{ item }">{{ item.currentPrice === null ? '—' : formatNumber(item.currentPrice) }}</template>
            <template v-slot:item.newPrice="{ item }">{{ item.newPrice === null ? '—' : formatNumber(item.newPrice) }}</template>
          </v-data-table>
          <v-alert v-if="importAppliedSummary" type="success" dense outlined class="mt-3">{{ importAppliedSummary }}</v-alert>
          <v-data-table v-if="importAppliedErrors.length" :headers="importErrorHeaders" :items="importAppliedErrors" dense class="mt-3" :items-per-page="5" />
          <div v-if="importError" class="error-text">{{ importError }}</div>
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn text @click="closeImport">Cancelar</v-btn><v-btn v-if="!importResult" color="primary" :loading="importLoading" :disabled="!importFile" @click="previewImport">Previsualizar</v-btn><v-btn v-else color="primary" :loading="importLoading" :disabled="!canApplyImport" @click="applyImport">Aplicar cambios</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { priceListsService } from "../services/priceLists";
import { productsService } from "../services/products";
import { warehousesService } from "../services/warehouses";
import { pricingStockService } from "../services/pricingStock";
import HelpHint from "@/components/HelpHint.vue";
import { parseApiError } from "@/utils/errors";

export default {
  name: "StockPriceList",
  components: { HelpHint },
  data() {
    return {
      lists: [],
      products: [],
      warehouses: [],
      providers: [],
      rubros: [],
      wizardDialog: false,
      wizardSaving: false,
      wizardForm: {
        scopeType: "all",
        providerId: "",
        rubro: "",
      },
      loading: false,
      loadingAllItems: false,
      dialog: false,
      itemsDialog: false,
      isEditing: false,
      valid: false,
      saving: false,
      error: "",
      headers: [
        { text: "Codigo", value: "code" },
        { text: "Nombre", value: "name" },
        { text: "Moneda", value: "currencyCode" },
        { text: "Decimales", value: "decimals" },
        { text: "Estado", value: "isActive" },
        { text: "Acciones", value: "actions", sortable: false },
      ],
      form: {
        id: "",
        code: "",
        name: "",
        shortDescription: "",
        currencyCode: "",
        includesTax: true,
        decimals: 2,
        isActive: true,
        attributes: "",
      },
      requiredRules: [(v) => Boolean(v) || "Campo requerido"],
      selectedList: null,
      itemsTab: 0,
      items: [],
      itemsLoading: false,
      itemHeaders: [
        { text: "Producto", value: "productId" },
        { text: "Precio", value: "price" },
        { text: "Moneda", value: "currencyCode" },
        { text: "Estado", value: "isActive" },
        { text: "Acciones", value: "actions", sortable: false },
      ],
      itemDialog: false,
      itemEditing: false,
      itemValid: false,
      itemSaving: false,
      itemError: "",
      itemFormState: {
        id: "",
        productId: "",
        price: 0,
        validFrom: "",
        validTo: "",
        currencyCode: "",
        isActive: true,
      },
      extendedItems: [],
      extendedLoading: false,
      extendedHeaders: [
        { text: "Codigo", value: "productCode" },
        { text: "Producto", value: "productName" },
        { text: "Precio", value: "price" },
        { text: "Disponible", value: "totalAvailable" },
      ],
      extendedFilters: {
        hasStock: "Todos",
        warehouseId: "",
        q: "",
      },
      hasStockOptions: ["Todos", "true", "false"],
      updates: [],
      updatesLoading: false,
      updatesError: "",
      confirmingUpdateId: "",
      updatesHeaders: [
        { text: "Descripción", value: "description" },
        { text: "Fecha", value: "createdAt" },
        { text: "Multiplicador", value: "multiplier" },
        { text: "Confirmada", value: "confirmed" },
        { text: "Acciones", value: "actions", sortable: false },
      ],
      historyEntries: [],
      historyLoading: false,
      historyError: "",
      historyHeaders: [
        { text: "Fecha", value: "changedAt" },
        { text: "Producto", value: "productLabel" },
        { text: "Origen", value: "changeSource" },
        { text: "Precio Anterior", value: "previousPrice" },
        { text: "Precio Nuevo", value: "newPrice" },
      ],
      historyFilters: {
        productId: "",
        limit: 100,
      },
      historySummary: null,
      exportLoading: false,
      exportFormat: "xlsx",
      exportFormatOptions: ["xlsx", "csv"],
      importDialog: false, importFile: null, importProductFields: false, importResult: null, importLoading: false, importError: "", importAppliedSummary: "", importAppliedErrors: [],
      importHeaders: [{ text: "Fila", value: "row" }, { text: "Código archivo", value: "code" }, { text: "Artículo maestro", value: "productName" }, { text: "Precio actual", value: "currentPrice" }, { text: "Precio nuevo", value: "newPrice" }, { text: "Resultado", value: "status" }],
      importErrorHeaders: [{ text: "Fila", value: "row" }, { text: "Código", value: "code" }, { text: "No actualizado", value: "reason" }],
    };
  },
  async created() {
    await Promise.all([this.fetchLists(), this.loadReferenceData()]);
  },
  watch: {
    itemsTab(val) {
      if (!this.selectedList) return;
      if (val === 1) {
        this.fetchExtended();
      } else if (val === 2) {
        this.fetchUpdates();
      } else if (val === 3) {
        this.fetchHistory();
      }
    },
  },
  methods: {
    extractData(response) {
      if (response && response.data && Object.prototype.hasOwnProperty.call(response.data, "data")) return response.data.data;
      return response && response.data ? response.data : null;
    },
    async loadReferenceData() {
      try {
        const [productsResp, warehousesResp, providersResp, rubrosResp] = await Promise.all([
          productsService.list("", { limit: 2000 }),
          warehousesService.list(),
          pricingStockService.getProviders(),
          pricingStockService.getRubrosCatalog(),
        ]);
        this.products = this.extractData(productsResp) || [];
        this.warehouses = this.extractData(warehousesResp) || [];
        this.providers = this.extractData(providersResp) || [];
        this.rubros = this.extractData(rubrosResp) || [];
      } catch (_error) {
        this.products = [];
        this.warehouses = [];
        this.providers = [];
        this.rubros = [];
      }
    },
    productLabelById(id) {
      const row = (this.products || []).find((item) => item.id === id);
      if (!row) return id;
      return `${row.code} - ${row.name}`;
    },
    async fetchLists() {
      this.loading = true;
      this.error = "";
      try {
        const response = await priceListsService.list();
        this.lists = this.extractData(response) || [];
      } catch (err) {
        this.error = parseApiError(err, "No pudimos cargar las listas.");
      } finally {
        this.loading = false;
      }
    },
    openCreate() {
      this.isEditing = false;
      this.dialog = true;
      this.error = "";
      this.form = {
        id: "",
        code: "",
        name: "",
        shortDescription: "",
        currencyCode: "",
        includesTax: true,
        decimals: 2,
        isActive: true,
        isVirtual: false,
        dependsOnListId: "",
        percentageAdjustment: 0,
        attributes: "",
      };
    },
    openEdit(item) {
      this.isEditing = true;
      this.dialog = true;
      this.error = "";
      let adjustment = 0;
      if (item.calculationExpression && item.calculationExpression.includes("price *")) {
        const factorStr = item.calculationExpression.split("price *")[1].trim();
        const factor = parseFloat(factorStr);
        if (!isNaN(factor)) {
          adjustment = Number(((factor - 1) * 100).toFixed(2));
        }
      }
      this.form = {
        id: item.id,
        code: item.code,
        name: item.name,
        shortDescription: item.shortDescription || "",
        currencyCode: item.currencyCode || "",
        includesTax: Boolean(item.includesTax),
        decimals: item.decimals || 2,
        isActive: Boolean(item.isActive),
        isVirtual: Boolean(item.isVirtual),
        dependsOnListId: item.dependsOnListId || "",
        percentageAdjustment: adjustment,
        attributes: item.attributes || "",
      };
    },
    closeDialog() {
      this.dialog = false;
    },
    async saveList() {
      const isValid = this.$refs.form.validate();
      if (!isValid) return;

      this.saving = true;
      this.error = "";

      let expression = null;
      if (this.form.isVirtual && this.form.dependsOnListId) {
        const multiplier = (1 + Number(this.form.percentageAdjustment || 0) / 100).toFixed(4);
        expression = `price * ${multiplier}`;
      }

      try {
        if (this.isEditing) {
          await priceListsService.update(this.form.id, {
            name: this.form.name,
            shortDescription: this.form.shortDescription,
            currencyCode: this.form.currencyCode,
            includesTax: this.form.includesTax,
            decimals: this.form.decimals,
            isActive: this.form.isActive,
            isVirtual: this.form.isVirtual,
            dependsOnListId: this.form.dependsOnListId || null,
            calculationExpression: expression,
            attributes: this.form.attributes || null,
          });
        } else {
          await priceListsService.create({
            code: this.form.code,
            name: this.form.name,
            shortDescription: this.form.shortDescription,
            currencyCode: this.form.currencyCode,
            includesTax: this.form.includesTax,
            decimals: this.form.decimals,
            isActive: this.form.isActive,
            isVirtual: this.form.isVirtual,
            dependsOnListId: this.form.dependsOnListId || null,
            calculationExpression: expression,
            attributes: this.form.attributes || null,
          });
        }
        this.dialog = false;
        await this.fetchLists();
      } catch (err) {
         this.error = parseApiError(err, "No pudimos guardar la lista.");
      } finally {
        this.saving = false;
      }
    },
    async openItems(item) {
      this.selectedList = item;
      this.itemsDialog = true;
      this.itemsTab = 0;
      await this.fetchItems();
    },
    async fetchItems() {
      if (!this.selectedList) return;
      this.itemsLoading = true;
      try {
        const response = await priceListsService.listItems(this.selectedList.id);
        this.items = this.extractData(response) || [];
      } catch (err) {
        this.itemError = parseApiError(err, "No pudimos cargar los items.");
      } finally {
        this.itemsLoading = false;
      }
    },
    openWizard() {
      this.wizardDialog = true;
      this.wizardSaving = false;
      this.wizardForm = {
        scopeType: "all",
        providerId: "",
        rubro: "",
      };
    },
    async applyWizardInitialize() {
      if (!this.selectedList) return;
      this.wizardSaving = true;
      try {
        const payload = {};
        if (this.wizardForm.scopeType === "provider") {
          payload.providerId = this.wizardForm.providerId || undefined;
        } else if (this.wizardForm.scopeType === "rubro") {
          payload.rubro = this.wizardForm.rubro || undefined;
        }
        await priceListsService.createAllMissingItems(this.selectedList.id, payload);
        this.wizardDialog = false;
        await this.fetchItems();
      } catch (err) {
        console.error(err);
      } finally {
        this.wizardSaving = false;
      }
    },
    openItemCreate() {
      this.itemEditing = false;
      this.itemDialog = true;
      this.itemError = "";
      this.itemFormState = {
        id: "",
        productId: "",
        price: 0,
        validFrom: "",
        validTo: "",
        currencyCode: "",
        isActive: true,
      };
    },
    openItemEdit(item) {
      this.itemEditing = true;
      this.itemDialog = true;
      this.itemError = "";
      this.itemFormState = {
        id: item.id || item.itemId,
        productId: item.productId,
        price: item.price,
        validFrom: item.validFrom || "",
        validTo: item.validTo || "",
        currencyCode: item.currencyCode || "",
        isActive: Boolean(item.isActive),
      };
    },
    async saveItem() {
      const isValid = this.$refs.itemForm.validate();
      if (!isValid) return;

      this.itemSaving = true;
      this.itemError = "";

      try {
        if (this.itemEditing) {
          await priceListsService.updateItem(this.selectedList.id, this.itemFormState.id, {
            price: this.itemFormState.price,
            validFrom: this.itemFormState.validFrom,
            validTo: this.itemFormState.validTo,
            currencyCode: this.itemFormState.currencyCode,
            isActive: this.itemFormState.isActive,
          });
        } else {
          await priceListsService.createItem(this.selectedList.id, {
            productId: this.itemFormState.productId,
            price: this.itemFormState.price,
            validFrom: this.itemFormState.validFrom,
            validTo: this.itemFormState.validTo,
            currencyCode: this.itemFormState.currencyCode,
            isActive: this.itemFormState.isActive,
          });
        }
        this.itemDialog = false;
        await this.fetchItems();
      } catch (err) {
        this.itemError = parseApiError(err, "No pudimos guardar el item.");
      } finally {
        this.itemSaving = false;
      }
    },
    async deleteItem(item) {
      try {
        await priceListsService.deleteItem(this.selectedList.id, item.id || item.itemId);
        await this.fetchItems();
      } catch (err) {
        this.itemError = parseApiError(err, "No pudimos eliminar el item.");
      }
    },
    async fetchExtended() {
      if (!this.selectedList) return;
      this.extendedLoading = true;
      try {
        const params = {
          warehouseId: this.extendedFilters.warehouseId || undefined,
          q: this.extendedFilters.q || undefined,
        };
        if (this.extendedFilters.hasStock !== "Todos") {
          params.hasStock = this.extendedFilters.hasStock;
        }
        const response = await priceListsService.listItemsExtended(this.selectedList.id, params);
        this.extendedItems = this.extractData(response) || [];
      } catch (err) {
        this.itemError = parseApiError(err, "No pudimos cargar la lista extendida.");
      } finally {
        this.extendedLoading = false;
      }
    },
    async exportExtended() {
      if (!this.selectedList) return;
      this.exportLoading = true;
      try {
        const params = {
          format: this.exportFormat,
          warehouseId: this.extendedFilters.warehouseId || undefined,
          q: this.extendedFilters.q || undefined,
        };
        if (this.extendedFilters.hasStock !== "Todos") params.hasStock = this.extendedFilters.hasStock;
        const response = await priceListsService.exportItems(this.selectedList.id, params);
        const mime =
          this.exportFormat === "csv"
            ? "text/csv;charset=utf-8;"
            : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        const blob = new Blob([response.data], { type: mime });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `price_list_${this.selectedList.code || this.selectedList.id}.${this.exportFormat}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        this.itemError = parseApiError(err, "No pudimos descargar la lista de precios.");
      } finally {
        this.exportLoading = false;
      }
    },
    async downloadImportTemplate() {
      // La exportación usa las columnas que el importador ERP reconoce: código,
      // precio y metadatos de la lista, para permitir editar y reimportar.
      const previousFormat = this.exportFormat;
      this.exportFormat = "xlsx";
      try {
        await this.exportExtended();
      } finally {
        this.exportFormat = previousFormat;
      }
    },
    async fetchUpdates() {
      if (!this.selectedList) return;
      this.updatesLoading = true;
      this.updatesError = "";
      try {
        const response = await priceListsService.listUpdates(this.selectedList.id);
        this.updates = this.extractData(response) || [];
      } catch (err) {
        this.updatesError = parseApiError(err, "No pudimos cargar las actualizaciones.");
      } finally {
        this.updatesLoading = false;
      }
    },
    async confirmUpdate(item) {
      this.confirmingUpdateId = item.id;
      try {
        await priceListsService.confirmUpdate(item.id);
        await this.fetchUpdates();
      } catch (err) {
        this.updatesError = parseApiError(err, "No pudimos confirmar la actualización.");
      } finally {
        this.confirmingUpdateId = "";
      }
    },
    async fetchHistory() {
      if (!this.selectedList) return;
      this.historyLoading = true;
      this.historyError = "";
      try {
        const params = {
          productId: this.historyFilters.productId || undefined,
          limit: this.historyFilters.limit || undefined,
        };
        const response = await priceListsService.listHistory(this.selectedList.id, params);
        const entries = this.extractData(response) || [];
        this.historyEntries = entries.map((entry) => ({
          ...entry,
          productLabel: `${entry.productCode || ""} ${entry.productName || ""}`.trim(),
        }));
      } catch (err) {
        this.historyError = parseApiError(err, "No pudimos cargar el historial.");
      } finally {
        this.historyLoading = false;
      }
    },
    formatDateTime(value) {
      if (!value) return "";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString();
    },
    formatNumber(value) {
      if (value === null || value === undefined) return "—";
      return new Intl.NumberFormat("es-AR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      }).format(Number(value));
    },
    openImport() { this.importDialog = true; this.importFile = null; this.importResult = null; this.importError = ""; this.importAppliedSummary = ""; this.importAppliedErrors = []; },
    closeImport() { this.importDialog = false; this.importFile = null; this.importResult = null; },
    async sendImport(preview) {
      const form = new FormData(); form.append("file", this.importFile); form.append("updateProductFields", String(this.importProductFields));
      if (!preview) form.append("previewHash", this.importResult.fileHash);
      const response = await priceListsService.uploadItemsFile(this.selectedList.id, form, { preview });
      return this.extractData(response);
    },
    async previewImport() { this.importLoading = true; this.importError = ""; try { this.importResult = await this.sendImport(true); } catch (err) { this.importError = parseApiError(err, "No pudimos leer el archivo."); } finally { this.importLoading = false; } },
    async applyImport() { this.importLoading = true; this.importError = ""; try { const result = await this.sendImport(false); await this.fetchItems(); this.importAppliedSummary = `Importación aplicada: ${result.updated} actualizados, ${result.created} creados y ${result.skipped} omitidos.`; this.importAppliedErrors = result.errors || []; } catch (err) { this.importError = parseApiError(err, "No se aplicó ningún cambio."); } finally { this.importLoading = false; } },
  },
  computed: {
    matchedImportRows() { return (this.importResult?.previewRows || []).filter((row) => row.status === "matched").length; },
    missingImportRows() { return (this.importResult?.previewRows || []).filter((row) => row.status === "not_found").length; },
    duplicateImportRows() { return (this.importResult?.previewRows || []).filter((row) => row.status === "duplicate_omitted").length; },
    canApplyImport() { return Boolean(this.importFile && this.importResult?.preview && this.importResult?.fileHash && !this.importAppliedSummary); },
    productOptions() {
      return (this.products || []).map((item) => ({ id: item.id, label: `${item.code} - ${item.name}` }));
    },
    warehouseOptions() {
      return (this.warehouses || []).map((item) => ({ id: item.id, label: `${item.code} - ${item.name}` }));
    },
    baseListsOptions() {
      return (this.lists || [])
        .filter((x) => !x.isVirtual && x.id !== this.form.id)
        .map((x) => ({ id: x.id, label: `${x.code} - ${x.name}` }));
    },
    providerOptions() {
      return (this.providers || []).map((p) => ({ id: p.id, label: `${p.code} - ${p.name}` }));
    },
    rubroOptions() {
      return (this.rubros || []).map((r) => ({ value: r.rubro, label: `${r.rubro} - ${r.description || 'Sin descripción'}` }));
    },
  },
};
</script>

<style scoped>
.page {
  padding: 0;
}
.v-data-table :deep(tbody td),
.v-data-table :deep(thead th) {
  height: 32px !important;
  padding: 2px 8px !important;
  font-weight: 500;
}

.page-hero {
  background: #ffffff;
  border-radius: 18px;
  padding: 20px 24px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.page-hero h2 {
  margin: 0 0 4px;
  font-size: 22px;
  font-weight: 700;
}

.page-hero p {
  margin: 0;
  color: #64748b;
}

.hero-btn {
  text-transform: none;
  font-weight: 700;
}

.page-card {
  border-radius: 18px;
  padding: 16px 20px 8px;
}

.table-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.table-header h3 {
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 700;
}

.table-header p {
  margin: 0;
  color: #64748b;
  font-size: 12px;
}

.pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.pill--success {
  background: rgba(22, 163, 74, 0.12);
  color: #16a34a;
}

.pill--warn {
  background: rgba(245, 158, 11, 0.12);
  color: #f59e0b;
}

.filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.error-text {
  margin-top: 12px;
  color: #dc2626;
}

.items-header {
  font-weight: 700;
}

@media (max-width: 960px) {
  .page-hero {
    flex-direction: column;
    align-items: flex-start;
  }
  .table-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
